import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { Search as SearchIcon, X } from "lucide-react";
import { getAllPosts } from "@/lib/posts.functions";
import { useLocalizedPosts } from "@/lib/wp.hooks";
import { useT } from "@/lib/i18n";
import { hreflangLinks } from "@/lib/seo";
import { pageTitle } from "@/lib/brand";
import { PageIntro, PageSection, Chip } from "@/components/site/page";
import { ArchiveRows, postsToArchiveItems } from "@/components/site/ArchiveRows";
import { THEME_DOT } from "@/components/site/theme-ui";
import { THEME_PAGES } from "@/lib/themes-public";
import { listApprovedThemes } from "@/lib/themes-public.functions";

type SortKey = "newest" | "oldest" | "relevance";
type SearchParams = {
  q?: string;
  cat?: string; // csv
  year?: number;
  sort?: SortKey;
};

const SUGGESTED_KEYWORDS = [
  "verimlilik",
  "greenwashing",
  "algoritma",
  "yapay zekâ",
  "iklim",
  "rebound",
  "jevons",
  "sürdürülebilirlik",
];

export const Route = createFileRoute("/search")({
  validateSearch: (s: Record<string, unknown>): SearchParams => ({
    q: typeof s.q === "string" ? s.q : undefined,
    cat: typeof s.cat === "string" ? s.cat : undefined,
    year:
      typeof s.year === "number"
        ? s.year
        : typeof s.year === "string" && /^\d{4}$/.test(s.year)
          ? Number(s.year)
          : undefined,
    sort:
      s.sort === "oldest" || s.sort === "relevance" || s.sort === "newest"
        ? s.sort
        : undefined,
  }),
  head: () => {
    const desc =
      "Arda Öztaşkın'ın yapay zeka, iklim ve sürdürülebilirlik üzerine denemeleri arasında başlık, özet, kategori, yıl ve anahtar kelimeye göre arama yapın.";
    const url = "https://akillisurdurulebilirlik.com/search";
    return {
      meta: [
        { title: pageTitle("Arama", "tr") },
        { name: "description", content: desc },
        { name: "robots", content: "noindex, follow" },
        { property: "og:title", content: "Arama — Akıllı Sürdürülebilirlik" },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/search")],
    };
  },
  loader: async () => {
    const [posts, themes] = await Promise.all([getAllPosts(), listApprovedThemes()]);
    return { posts, themes };
  },
  component: SearchPage,
});

function norm(s: string) {
  return s
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function scoreArticle(a: { title: string; excerpt: string; category: string }, needle: string) {
  const t = norm(a.title);
  const e = norm(a.excerpt);
  const c = norm(a.category);
  let s = 0;
  if (t.includes(needle)) s += 10;
  if (t.startsWith(needle)) s += 5;
  if (e.includes(needle)) s += 3;
  if (c.includes(needle)) s += 2;
  return s;
}

function SearchPage() {
  const { t } = useT();
  const { q, cat, year, sort } = Route.useSearch();
  const navigate = useNavigate({ from: "/search" });
  const data = Route.useLoaderData();
  const { articles, isTranslating } = useLocalizedPosts(data.posts);
  const themeOf = useMemo(() => new Map(data.themes.map((t) => [t.slug, t.primary as string])), [data.themes]);
  const [input, setInput] = useState(q ?? "");

  useEffect(() => {
    setInput(q ?? "");
  }, [q]);

  const activeCats = useMemo(
    () => (cat ? cat.split(",").filter(Boolean) : []),
    [cat],
  );
  const activeSort: SortKey = sort ?? "relevance";

  // Facets derived from full corpus
  const { categories, years } = useMemo(() => {
    const catCounts = new Map<string, number>();
    const yearSet = new Set<number>();
    for (const a of articles) {
      const th = themeOf.get(a.slug);
      if (th) catCounts.set(th, (catCounts.get(th) ?? 0) + 1);
      const y = new Date(a.isoDate).getFullYear();
      if (!Number.isNaN(y)) yearSet.add(y);
    }
    return {
      categories: THEME_PAGES.map((t) => [t.key, catCounts.get(t.key) ?? 0, t.title] as const),
      years: Array.from(yearSet).sort((a, b) => b - a),
    };
  }, [articles, themeOf]);

  const query = (q ?? "").trim();

  const results = useMemo(() => {
    const needle = query ? norm(query) : null;
    const filtered = articles.filter((a) => {
      if (activeCats.length && !activeCats.includes(themeOf.get(a.slug) ?? "")) return false;
      if (year) {
        const y = new Date(a.isoDate).getFullYear();
        if (y !== year) return false;
      }
      if (needle) {
        const hay = norm(`${a.title} ${a.excerpt} ${a.category}`);
        if (!hay.includes(needle)) return false;
      }
      return true;
    });

    const sorted = [...filtered];
    if (activeSort === "newest" || (activeSort === "relevance" && !needle)) {
      sorted.sort((a, b) => +new Date(b.isoDate) - +new Date(a.isoDate));
    } else if (activeSort === "oldest") {
      sorted.sort((a, b) => +new Date(a.isoDate) - +new Date(b.isoDate));
    } else if (activeSort === "relevance" && needle) {
      sorted.sort((a, b) => scoreArticle(b, needle) - scoreArticle(a, needle));
    }
    return sorted;
  }, [articles, activeCats, year, activeSort, query, themeOf]);

  const update = (patch: Partial<SearchParams>) => {
    navigate({
      search: (prev: SearchParams) => {
        const next = { ...prev, ...patch };
        for (const k of Object.keys(next) as (keyof SearchParams)[]) {
          const v = next[k];
          if (v === "" || v === undefined || v === null) delete next[k];
        }
        return next;
      },
    });
  };

  const toggleCat = (c: string) => {
    const set = new Set(activeCats);
    if (set.has(c)) set.delete(c);
    else set.add(c);
    const csv = Array.from(set).join(",");
    update({ cat: csv || undefined });
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    update({ q: input.trim() || undefined });
  };

  const hasFilters =
    Boolean(query) || activeCats.length > 0 || Boolean(year) || (sort && sort !== "relevance");

  return (
    <div>
      <PageIntro
        crumbs={[{ label: t("search.title") }]}
        kicker={t("search.title")}
        title={
          query ? (
            <>
              {t("search.results_for")} <em className="italic">"{query}"</em>
            </>
          ) : (
            t("search.title")
          )
        }
      >
        <form onSubmit={onSubmit} className="relative mt-6 max-w-2xl">
          <SearchIcon aria-hidden className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-ink/70" />
          <input
            type="search"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t("search.placeholder")}
            aria-label={t("search.placeholder")}
            autoFocus
            className="min-h-11 w-full rounded-full border border-ink/20 bg-paper pl-11 pr-28 text-base focus:border-ink focus:outline-none"
          />
          <button
            type="submit"
            className="absolute right-1.5 top-1/2 inline-flex min-h-9 -translate-y-1/2 items-center rounded-full bg-ink px-5 text-sm text-paper hover:bg-ink/85"
          >
            {t("nav.search")}
          </button>
        </form>
      </PageIntro>

      <PageSection title={t("search.suggested")}>
        <div className="flex flex-wrap gap-2">
          {SUGGESTED_KEYWORDS.map((k) => {
            const active = norm(query) === norm(k);
            return (
              <Chip
                key={k}
                active={active}
                onClick={() => {
                  setInput(k);
                  update({ q: active ? undefined : k });
                }}
              >
                {k}
              </Chip>
            );
          })}
        </div>
      </PageSection>

      <PageSection
        title={t("search.filters")}
        link={
          hasFilters ? (
            <button
              type="button"
              onClick={() => {
                setInput("");
                navigate({ search: {} });
              }}
              className="inline-flex min-h-10 items-center gap-1 text-sm text-ink/60 hover:text-ink"
            >
              <X className="size-3.5" /> {t("search.clear")}
            </button>
          ) : undefined
        }
      >
        <div className="space-y-5">
          <div>
            <p className="mb-2 text-sm text-ink/60">{t("search.filter_category")}</p>
            <div className="flex flex-wrap gap-2">
              {categories.map(([c, n, label]) => (
                <Chip key={c} active={activeCats.includes(c)} onClick={() => toggleCat(c)} count={n} dot={THEME_DOT[c]}>
                  {label}
                </Chip>
              ))}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <p className="mb-2 text-sm text-ink/60">{t("search.filter_year")}</p>
              <div className="flex flex-wrap gap-2">
                <Chip active={!year} onClick={() => update({ year: undefined })}>
                  {t("search.filter_all")}
                </Chip>
                {years.map((y) => (
                  <Chip key={y} active={year === y} onClick={() => update({ year: y === year ? undefined : y })}>
                    {String(y)}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-sm text-ink/60">{t("search.filter_sort")}</p>
              <div className="flex flex-wrap gap-2">
                {(["relevance", "newest", "oldest"] as SortKey[]).map((s) => (
                  <Chip key={s} active={activeSort === s} onClick={() => update({ sort: s === "relevance" ? undefined : s })}>
                    {t(`search.sort_${s}` as const)}
                  </Chip>
                ))}
              </div>
            </div>
          </div>
        </div>
      </PageSection>

      <PageSection>
        {!query && activeCats.length === 0 && !year ? (
          <p className="text-ink/60">{t("search.empty")}</p>
        ) : (
          <>
            <p className="mb-6 flex flex-wrap items-center gap-3 text-sm text-ink/70" aria-live="polite">
              {results.length} {t("search.count")}
              {isTranslating && (
                <span className="inline-flex items-center gap-2 rounded-full border border-theme-tech/40 bg-theme-tech/10 px-3 py-1 font-medium text-theme-tech">
                  <span className="inline-block size-1.5 animate-pulse rounded-full bg-theme-tech" />
                  {t("common.translating")}
                </span>
              )}
            </p>

            {results.length === 0 ? (
              <p className="text-ink/60">{t("search.no_results")}</p>
            ) : (
              <div className="border-t border-ink/10"><ArchiveRows items={postsToArchiveItems(results, data.themes)} /></div>
            )}
          </>
        )}
      </PageSection>
    </div>
  );
}
